/**
 * Recently Viewed rail controller.
 *
 * - Tracks the current product handle in localStorage on every PDP visit
 *   (keyed to <body li-page="product">).
 * - On any surface containing `[data-recently-viewed-section]`, fetches each
 *   stored product via Shopify's product .js endpoint and renders cards into
 *   `[data-recently-viewed-list]`, capped at `data-limit`, excluding the
 *   current product id when supplied.
 * - Toggles the `[data-recently-viewed-empty]` placeholder and hides the
 *   surrounding section entirely when nothing renders.
 *
 * The card markup mirrors snippets/product_item.liquid so the Fortis CSS
 * bundle styles apply with no additional rules.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'carrolls_recently_viewed';
  var MAX_STORED = 12;
  var SECTION_SELECTOR = '[data-recently-viewed-section]';

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (h) {
        return typeof h === 'string' && h.length > 0 && h.length < 200;
      });
    } catch (err) {
      return [];
    }
  }

  function writeList(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (err) {
      // Quota or privacy-mode failures are non-fatal.
    }
  }

  function currentProductHandle() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (meta && meta.product && meta.product.handle) return meta.product.handle;
    if (window.meta && window.meta.product && window.meta.product.handle) return window.meta.product.handle;
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? match[1] : null;
  }

  function trackCurrentProduct() {
    if (document.body.getAttribute('li-page') !== 'product') return;
    var handle = currentProductHandle();
    if (!handle) return;
    var list = readList().filter(function (h) { return h !== handle; });
    list.unshift(handle);
    writeList(list);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function moneyFormat(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try { return window.Shopify.formatMoney(cents, window.Shopify.money_format); } catch (err) {}
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function lowestAvailablePrice(product) {
    var variants = (product.variants || []).filter(function (v) { return v.available; });
    if (!variants.length) return product.price || 0;
    return variants.reduce(function (min, v) { return v.price < min ? v.price : min; }, variants[0].price);
  }

  function featuredImage(product) {
    if (product.featured_image && typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.src) return product.featured_image.src;
    if (Array.isArray(product.images) && product.images.length) {
      var first = product.images[0];
      return typeof first === 'string' ? first : (first && first.src) || '';
    }
    return '';
  }

  function resizeShopifyImage(url, width) {
    if (!url) return '';
    return url.replace(/(_(pico|icon|thumb|small|compact|medium|large|grande|original|master))?\.(jpe?g|png|webp|gif)(\?.*)?$/i, function (_, _suffix, _name, ext, query) {
      return '_' + width + 'x.' + ext + (query || '');
    });
  }

  function cardMarkup(product) {
    var image = featuredImage(product);
    var imageUrl = resizeShopifyImage(image, 800);
    var price = lowestAvailablePrice(product);
    var vendor = (product.vendor || '').trim();
    var url = product.url || ('/products/' + product.handle);
    var title = product.title || '';

    return [
      '<li class="product_list-item" role="listitem">',
      '<div class="card_product">',
      '<div class="product-item_wrapper">',
      '<div class="card_product-media">',
      product.available === false ? '<div class="product-item_tag-item"><div class="card_product-tag">Sold Out</div></div>' : '',
      imageUrl
        ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="800" height="800" class="card_product-image">'
        : '',
      '</div>',
      '<div class="product-item_content">',
      vendor ? '<div class="card_product-brand">' + escapeHtml(vendor) + '</div>' : '',
      '<div class="card_product-title">' + escapeHtml(title) + '</div>',
      price ? '<div class="card_product-price">' + escapeHtml(moneyFormat(price)) + '</div>' : '',
      '</div>',
      '</div>',
      '<a aria-label="' + escapeHtml(title) + '" href="' + escapeHtml(url) + '" class="product-item_link w-inline-block"></a>',
      '</div>',
      '</li>',
    ].join('');
  }

  function fetchProduct(handle) {
    return window
      .fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; });
  }

  function renderSection(section) {
    var list = section.querySelector('[data-recently-viewed-list]');
    var empty = section.querySelector('[data-recently-viewed-empty]');
    var rail = section.querySelector('[data-recently-viewed-rail]');
    if (!list) return Promise.resolve();

    var limit = parseInt((rail && rail.getAttribute('data-limit')) || '6', 10) || 6;
    var excludeId = (rail && rail.getAttribute('data-exclude-product-id')) || '';
    var currentHandle = currentProductHandle();
    var handles = readList().filter(function (h) { return h !== currentHandle; });

    if (!handles.length) {
      if (empty) empty.removeAttribute('hidden');
      section.removeAttribute('hidden');
      return Promise.resolve();
    }

    return Promise.all(handles.slice(0, limit + 2).map(fetchProduct)).then(function (results) {
      var products = results
        .filter(function (p) { return p && p.id; })
        .filter(function (p) { return !excludeId || String(p.id) !== String(excludeId); })
        .slice(0, limit);

      if (!products.length) {
        if (empty) empty.removeAttribute('hidden');
        return;
      }
      if (empty) empty.setAttribute('hidden', '');
      list.classList.add('rec_slider');
      list.innerHTML = products.map(cardMarkup).join('');
    });
  }

  function init() {
    trackCurrentProduct();
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    if (!sections.length) return;
    Array.prototype.forEach.call(sections, function (section) {
      window.requestAnimationFrame(function () { renderSection(section); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
